import cadex from '@cadexchanger/web-toolkit'
import { Point3dDto } from 'services/APIs/InternalAPI/internal-api.contracts'
import {
  DistanceMeasurementOptions,
  MeasurementsManager,
} from './MeasurementsManager'

export enum MeasurementMode {
  TwoPointDistance = 0,
  ThreePointAngle = 1,
}

export class BoundingBoxManager {
  private scene: cadex.ModelPrs_Scene
  private boundingBoxPoints: cadex.ModelData_Point[]
  private measurementManager: MeasurementsManager
  private boundingBoxNode: cadex.ModelPrs_SceneNode

  constructor(
    scene: cadex.ModelPrs_Scene,
    points: Point3dDto[],
    lengthUnit: cadex.Base_LengthUnit
  ) {
    this.scene = scene

    this.measurementManager = new MeasurementsManager(
      this.scene,
      12,
      lengthUnit
    )
    this.setBoundingBoxPoints(points)
  }

  private setBoundingBoxPoints(points: Point3dDto[]) {
    this.boundingBoxPoints = points.map((point) => {
      return new cadex.ModelData_Point(point.x, point.y, point.z)
    })
  }

  private createSideExtensionOptions(
    start: cadex.ModelData_Point,
    end: cadex.ModelData_Point,
    extensionLineLength = 0
  ): DistanceMeasurementOptions {
    const extensionVector = new cadex.ModelData_Vector().subtractVectors(
      end,
      start
    )

    return {
      extensionLineDirection: cadex.ModelData_Direction.fromXYZ(
        extensionVector
      ),
      extensionLineLength,
    }
  }

  public async createBoundingBox(lengthUnit: cadex.Base_LengthUnit) {
    const pointsPairs = [
      [this.boundingBoxPoints[0], this.boundingBoxPoints[1]],
      [this.boundingBoxPoints[1], this.boundingBoxPoints[2]],
      [this.boundingBoxPoints[2], this.boundingBoxPoints[3]],
      [this.boundingBoxPoints[4], this.boundingBoxPoints[5]],
      [this.boundingBoxPoints[5], this.boundingBoxPoints[6]],
      [this.boundingBoxPoints[6], this.boundingBoxPoints[7]],
      [this.boundingBoxPoints[7], this.boundingBoxPoints[4]],

      // vertical lines
      [this.boundingBoxPoints[3], this.boundingBoxPoints[7]],
      [this.boundingBoxPoints[2], this.boundingBoxPoints[6]],
      [this.boundingBoxPoints[1], this.boundingBoxPoints[5]],
      [this.boundingBoxPoints[0], this.boundingBoxPoints[4]],
      [this.boundingBoxPoints[4], this.boundingBoxPoints[5]],
      [this.boundingBoxPoints[0], this.boundingBoxPoints[3]],
    ]

    this.measurementManager.clear()

    const widthPoints = [this.boundingBoxPoints[0], this.boundingBoxPoints[1]]
    const lengthPoints = [this.boundingBoxPoints[1], this.boundingBoxPoints[2]]
    const heightPoints = [this.boundingBoxPoints[0], this.boundingBoxPoints[4]]
    const widthExtensionOptions = this.createSideExtensionOptions(
      this.boundingBoxPoints[2],
      this.boundingBoxPoints[1]
    )
    const lengthExtensionOptions = this.createSideExtensionOptions(
      this.boundingBoxPoints[0],
      this.boundingBoxPoints[1]
    )
    const heightExtensionOptions = this.createSideExtensionOptions(
      this.boundingBoxPoints[1],
      this.boundingBoxPoints[0]
    )

    const widthMeasurement =
      await this.measurementManager.createDistanceMeasurement(
        widthPoints[0],
        widthPoints[1],
        lengthUnit,
        widthExtensionOptions
      )

    const lengthMeasurement =
      await this.measurementManager.createDistanceMeasurement(
        lengthPoints[0],
        lengthPoints[1],
        lengthUnit,
        lengthExtensionOptions
      )

    const heightMeasurement =
      await this.measurementManager.createDistanceMeasurement(
        heightPoints[0],
        heightPoints[1],
        lengthUnit,
        heightExtensionOptions
      )

    const higherMeasurent = [
      widthMeasurement,
      lengthMeasurement,
      heightMeasurement,
    ].sort((a, b) => b.value - a.value)[0]

    let fontSize = 60

    if (higherMeasurent.value > 850) {
      fontSize = Math.max(60, higherMeasurent.value % 191)
    } else if (higherMeasurent.value > 500 || higherMeasurent.value > 20) {
      fontSize = 60
    } else if (higherMeasurent.value > 200 || higherMeasurent.value > 8) {
      fontSize = 21
    } else if (higherMeasurent.value > 100 || higherMeasurent.value > 4) {
      fontSize = 15
    }

    const measurementOffset = 1.5 * fontSize

    widthMeasurement.fontSize = fontSize
    widthMeasurement.extensionLineLength = measurementOffset

    lengthMeasurement.fontSize = fontSize
    lengthMeasurement.extensionLineLength = measurementOffset

    heightMeasurement.fontSize = fontSize
    heightMeasurement.extensionLineLength = measurementOffset

    await this.measurementManager.update([
      widthMeasurement,
      lengthMeasurement,
      heightMeasurement,
    ])

    const polyLineSet = cadex.ModelData_PolyLineSet.create(pointsPairs)

    const graphNode =
      new cadex.ModelPrs_SceneNodeFactory().createNodeFromPolyVertexSet(
        polyLineSet
      )

    graphNode.displayMode = cadex.ModelPrs_DisplayMode.Shaded
    graphNode.selectionMode = cadex.ModelPrs_SelectionMode.Edge

    this.boundingBoxNode = graphNode

    this.scene.addRoot(this.boundingBoxNode)

    this.scene.update()
  }

  public clear() {
    this.measurementManager.clear()
    this.scene.removeRoot(this.boundingBoxNode)
    this.scene.update()
  }
}
